import React, { useState, useEffect, useCallback, useRef } from 'react';
import studentsService from '../../services/studentsService';
import communicationService from '../../services/communicationService';
import './TeacherSelector.css';

/**
 * StaffParentSelector - "message a parent" picker for staff roles that
 * don't have a "my classes" concept the way teachers do (HR, Nurse,
 * Registrar, Storekeeper). Two steps:
 *   1. Search all students in the school by name, pick one.
 *   2. Pick one of that student's parents/guardians to message.
 *
 * Reuses TeacherSelector.css's classes for a consistent look with the
 * other selector, and calls the same onSelected(userId, name) contract
 * ChatContainer expects from every selector.
 *
 * @param {function} onSelected - (userId, parentName) => void
 * @param {function} onCancel
 */
const StaffParentSelector = ({ onSelected, onCancel }) => {
  const [query, setQuery] = useState('');
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [parents, setParents] = useState([]);
  const [loadingParents, setLoadingParents] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [error, setError] = useState(null);

  const debounceRef = useRef(null);

  useEffect(() => {
    if (!query.trim()) {
      setStudents([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoadingStudents(true);
        setError(null);
        const data = await studentsService.listStudents({ search: query.trim(), limit: 10 });
        setStudents(data.items || []);
      } catch (err) {
        setError(`Failed to search students: ${err.message}`);
      } finally {
        setLoadingStudents(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handlePickStudent = useCallback(async (student) => {
    setSelectedStudent(student);
    setSelectedParent(null);
    setParents([]);
    try {
      setLoadingParents(true);
      setError(null);
      const data = await communicationService.getParentsByStudent(student.id);
      setParents(data.items || []);
    } catch (err) {
      setError(`Failed to load parents: ${err.message}`);
    } finally {
      setLoadingParents(false);
    }
  }, []);

  const handleConfirm = useCallback(() => {
    if (!selectedParent) {
      setError('Please select a parent');
      return;
    }
    const parentName = `${selectedParent.first_name || 'Unknown'} ${selectedParent.last_name || 'Parent'}`;
    onSelected(selectedParent.user_id, parentName);
  }, [selectedParent, onSelected]);

  return (
    <div className="selector-overlay">
      <div className="selector-modal">
        <div className="selector-header">
          <h2>Select a Parent</h2>
          <button className="selector-close" onClick={onCancel} aria-label="Close">✕</button>
        </div>

        <div className="selector-content">
          <div className="selector-section">
            <label className="selector-label">Find a Student</label>
            <div className="selector-controls">
              <input
                type="text"
                className="selector-search"
                placeholder="Search by student name..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          {error && (
            <div className="selector-error">
              <span>{error}</span>
              <button className="error-close" onClick={() => setError(null)} aria-label="Close error">✕</button>
            </div>
          )}

          <div className="selector-list">
            {loadingStudents ? (
              <div className="selector-loading">
                <div className="chat-spinner"></div>
                <p>Searching...</p>
              </div>
            ) : !query.trim() ? (
              <div className="selector-empty">Start typing a student's name to search</div>
            ) : students.length === 0 ? (
              <div className="selector-empty">No students found</div>
            ) : (
              students.map(student => (
                <div
                  key={student.id}
                  className={`selector-item ${selectedStudent?.id === student.id ? 'selected' : ''}`}
                  onClick={() => handlePickStudent(student)}
                >
                  <div className="selector-avatar">
                    {(student.first_name || 'U').charAt(0)}{(student.last_name || 'S').charAt(0)}
                  </div>
                  <div className="selector-info">
                    <div className="selector-name">{student.first_name} {student.last_name}</div>
                    {student.class_name && <div className="selector-subject">{student.class_name}</div>}
                  </div>
                  {selectedStudent?.id === student.id && <div className="selector-checkmark">✓</div>}
                </div>
              ))
            )}
          </div>

          {selectedStudent && (
            <div className="selector-section">
              <label className="selector-label">Parents/Guardians of {selectedStudent.first_name}</label>
              <div className="selector-list">
                {loadingParents ? (
                  <div className="selector-loading">
                    <div className="chat-spinner"></div>
                    <p>Loading parents...</p>
                  </div>
                ) : parents.length === 0 ? (
                  <div className="selector-empty">No messageable parent found for this student</div>
                ) : (
                  parents.map(parent => (
                    <div
                      key={parent.parent_id}
                      className={`selector-item ${selectedParent?.parent_id === parent.parent_id ? 'selected' : ''}`}
                      onClick={() => setSelectedParent(parent)}
                    >
                      <div className="selector-avatar">
                        {(parent.first_name || 'U').charAt(0)}{(parent.last_name || 'P').charAt(0)}
                      </div>
                      <div className="selector-info">
                        <div className="selector-name">{parent.first_name} {parent.last_name}</div>
                        {parent.relationship && <div className="selector-subject">{parent.relationship}</div>}
                      </div>
                      {selectedParent?.parent_id === parent.parent_id && <div className="selector-checkmark">✓</div>}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="selector-footer">
          <button className="selector-btn-cancel" onClick={onCancel}>Cancel</button>
          <button
            className={`selector-btn-confirm ${selectedParent ? 'active' : 'disabled'}`}
            onClick={handleConfirm}
            disabled={!selectedParent}
          >
            Message {selectedParent ? `${selectedParent.first_name}` : 'Parent'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StaffParentSelector;
